// profiles-gate — a decisão PURA da tela de perfis (TV e mobile): o que mostrar e
// quando "criar perfil" pode ser oferecido. Testável sem DOM (mesmo padrão de
// mobile-profile.ts / account-source.ts).
//
// ── O defeito que isto fecha (10/10/2026) ──
// A tela entrava no MODO CRIAÇÃO assim que a lista LOCAL vinha vazia, ANTES de o pull
// dos perfis da conta responder, e ficava nele se o pull falhasse. Um 2º aparelho na
// mesma conta caía num formulário de "crie seu primeiro perfil" com a conta já tendo
// perfil: criava um DUPLICADO (em prod, a conta do dono tem P1 e P2 com o mesmo nome; o
// P2 nasceu 54 s depois do 1º login de um Android) e ficava sem o histórico do P1.
//
// ── O invariante ──
// Com conta Swipe, "criar perfil" só existe DEPOIS de um pull de perfis que deu certo
// nesta sessão. Pendente → carregando. Falhou → "tentar novamente". Sem conta Swipe
// (ou lista direta, que não sincroniza) a lista local é a verdade, como sempre foi.

/** Estado do pull de perfis da conta, do ponto de vista da tela. */
export type ProfilesPullState =
  /** Pull em voo (o 1º desta sessão): a lista local ainda não diz nada sobre a conta. */
  | 'pending'
  /** O servidor respondeu nesta sessão: a lista é a da conta. */
  | 'ok'
  /** O pull falhou: não dá pra afirmar que a conta não tem perfis. */
  | 'failed'
  /** Sem conta Swipe / sem contexto de sync (lista direta): a lista local é a verdade. */
  | 'local'

/** O que a tela renderiza. */
export type ProfilesView = 'loading' | 'retry' | 'create' | 'list' | 'manage'

export interface ProfilesViewInput {
  /** Perfis na tela (`null` = a leitura local ainda não voltou). */
  profileCount: number | null
  pull: ProfilesPullState
  /** O modo que o USUÁRIO escolheu (abrir criação / gerenciar). */
  userMode: 'list' | 'create' | 'manage'
  maxProfiles: number
  /**
   * Quantos perfis VIVOS a conta tem segundo o pull (`remoteIds.size`), ou `null` quando o
   * pull não disse (sem conta Swipe, pull pendente/falho). Rodada 6: é ISTO, e não a lista
   * local, que autoriza "criar o primeiro perfil" — a leitura local que falha devolve `[]`
   * e a tela oferecia criar com a conta tendo perfil (o duplicado).
   */
  accountProfileCount: number | null
}

export interface ProfilesViewDecision {
  view: ProfilesView
  /** O cartão/botão "novo perfil" pode aparecer? */
  canCreate: boolean
}

/** A lista da tela já pode ser tomada como a da conta (ou a local é a verdade)? */
export function pullAllowsCreate(pull: ProfilesPullState): boolean {
  return pull === 'ok' || pull === 'local'
}

/**
 * Decide a tela. Ordem: leitura local pendente → carregando; pull pendente →
 * carregando (NUNCA criação); pull falho → tentar novamente; lista vazia confirmada →
 * criação obrigatória; senão o modo do usuário (criação só se `canCreate`).
 */
export function decideProfilesView(input: ProfilesViewInput): ProfilesViewDecision {
  const { profileCount, pull, userMode, maxProfiles } = input
  if (profileCount === null) return { view: 'loading', canCreate: false }
  const allowsCreate = pullAllowsCreate(pull)
  const canCreate = allowsCreate && profileCount < maxProfiles
  if (pull === 'pending') return { view: 'loading', canCreate: false }
  if (pull === 'failed') return { view: 'retry', canCreate: false }
  if (profileCount === 0) {
    // Lista local VAZIA com a conta TENDO perfis (a leitura local falhou, ou a adoção não
    // gravou): criar seria o duplicado. "Tentar novamente" refaz o pull e a leitura.
    if (pull === 'ok' && input.accountProfileCount !== null && input.accountProfileCount > 0) {
      return { view: 'retry', canCreate: false }
    }
    return { view: 'create', canCreate }
  }
  if (userMode === 'create') return canCreate ? { view: 'create', canCreate } : { view: 'list', canCreate }
  return { view: userMode, canCreate }
}

/**
 * Ordena pra tela: os perfis da CONTA primeiro (na ordem de criação), depois os que só
 * existem neste aparelho. Sem `remoteIds` (pull não feito / modo local) a ordem fica.
 *
 * Por que não esconder os locais: um perfil só-local pode ser o de uma instalação
 * antiga (base instalada antes do sync) com o histórico DESTE aparelho; esconder era
 * perder o acesso a ele. Mas é ele que, escolhido por engano, vira o duplicado na conta
 * (o flusher provisiona o perfil na 1ª escrita). Então ele fica, depois, e o foco
 * inicial nunca cai nele quando há perfil da conta (ver `initialFocusProfile`).
 */
export function orderAccountFirst<T extends { id: string }>(
  profiles: ReadonlyArray<T>,
  remoteIds: ReadonlySet<string> | null,
): T[] {
  if (!remoteIds) return [...profiles]
  const account = profiles.filter((p) => remoteIds.has(p.id))
  const localOnly = profiles.filter((p) => !remoteIds.has(p.id))
  return [...account, ...localOnly]
}

/**
 * O perfil que recebe o foco inicial. Com os perfis da conta conhecidos, o "último
 * usado neste aparelho" só vale se for DA CONTA; senão o 1º da conta. É o "PC que já
 * foi usado antes": ele lembrava um perfil LOCAL e focava nele, e um OK no controle
 * entrava num perfil sem o histórico da conta.
 */
export function initialFocusProfile<T extends { id: string }>(
  profiles: ReadonlyArray<T>,
  remoteIds: ReadonlySet<string> | null,
  lastId: string | null | undefined,
): T | null {
  if (profiles.length === 0) return null
  if (remoteIds && remoteIds.size > 0) {
    const last = lastId ? profiles.find((p) => p.id === lastId && remoteIds.has(p.id)) : undefined
    return last ?? profiles.find((p) => remoteIds.has(p.id)) ?? profiles[0] ?? null
  }
  return profiles.find((p) => p.id === lastId) ?? profiles[0] ?? null
}

/** O que a superfície MOBILE faz com o perfil ativo depois de puxar os da conta. */
export type MobileProfileDecision<T> =
  | { kind: 'keep' }
  | { kind: 'adopt'; profile: T }
  | { kind: 'ask' }
  | { kind: 'none' }

/**
 * A escolha do perfil no celular — regra do dono (10/10/2026): "pergunta 1 vez se
 * houver mais de um". O celular não tem a tela "quem está assistindo?" da TV, e o "1º
 * adulto automático" de antes podia gravar o histórico no perfil de um familiar.
 *  - o perfil ativo já é DA CONTA → mantém;
 *  - a conta não tem perfil → nenhum (o celular mostra o aviso de escolher/criar);
 *  - o último usado NESTE aparelho é da conta e sem PIN → adota (a escolha lembrada);
 *  - a conta tem UM perfil só, adulto e sem PIN → adota sem perguntar;
 *  - qualquer outro caso (2+ perfis, um só Infantil, um só com PIN) → pergunta, uma vez.
 * O Infantil nunca é adotado por padrão, e perfil com PIN nunca entra sem o PIN.
 * Perfil só-local (fora de `remoteIds`) nunca é candidato: escolhido, viraria um
 * duplicado na conta.
 */
export function decideMobileProfile<T extends { id: string; kind: 'adult' | 'kids'; hasPin?: boolean }>(
  profiles: ReadonlyArray<T>,
  remoteIds: ReadonlySet<string>,
  activeId: string | null | undefined,
  lastId: string | null | undefined,
): MobileProfileDecision<T> {
  const account = profiles.filter((p) => remoteIds.has(p.id))
  if (activeId && account.some((p) => p.id === activeId)) return { kind: 'keep' }
  if (account.length === 0) return { kind: 'none' }
  const last = lastId ? account.find((p) => p.id === lastId && !p.hasPin) : undefined
  if (last) return { kind: 'adopt', profile: last }
  const only = account.length === 1 ? account[0] : undefined
  if (only && only.kind === 'adult' && !only.hasPin) return { kind: 'adopt', profile: only }
  return { kind: 'ask' }
}
